import { Colors, Theme } from '@app/theme';
import mapValues from 'lodash/mapValues';
import { createUseStyles, useTheme } from 'react-jss';
import { NavLink, useLocation } from 'react-router-dom';
import classnames from 'classnames';
import { useNavbarColor } from './NavbarColorContext';
import { NAVBAR_TOOLTIP_ID } from './NavbarTooltip';
import { useTranslation } from 'react-i18next';
import { NotificationPin, GenericButton } from '@app/core';

const useStyles = createUseStyles((theme: Theme) => ({
  iconContainer: {
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    width: theme.marginBase * 5,
    height: theme.marginBase * 5,
    cursor: 'pointer',
    position: 'relative',
    borderRadius: theme.borderRadius.std,
  },
  icon: {
    fontSize: theme.iconSize.large,
    color: theme.colors.grayBorder,
  },
  tandemzLogo: {
    ...theme.flexCenter,

    width: theme.height.small,
    height: theme.height.small,
    borderRadius: theme.marginBase * 2.5,

    backgroundColor: theme.colors.white,
    marginBottom: theme.marginBase * 3,
  },
}));

const useActiveColors = createUseStyles((theme: Theme) =>
  mapValues(theme.colorDefinitions, (color) => ({
    backgroundColor: color.std,
    '& > i': {
      color: theme.colors.white,
    },
  })),
);

const useColors = createUseStyles((theme: Theme) =>
  mapValues(theme.colorDefinitions, (color) => ({
    '& > i': {
      color: color.std,
    },
  })),
);

interface Props {
  icon: string;
  to?: string;
  label?: string;
  others?: string[];
  notification?: boolean;
  onClick?: () => void;
  end?: boolean;
  color?: Colors;
  emph?: boolean;
}

export const NavbarLink = ({
  icon,
  to,
  others = [],
  label,
  notification,
  color: propsColor,
  emph,
  ...rest
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const activeClasses = useActiveColors({ theme });
  const notActiveClasses = useColors({ theme });
  const location = useLocation();

  const navColor = useNavbarColor();
  const color = propsColor || navColor;

  if (!to) {
    return (
      <GenericButton
        {...rest}
        className={classnames(classes.iconContainer, {
          [notActiveClasses[color]]: !!emph,
        })}
        data-tip={label ? label : undefined}
        data-for={NAVBAR_TOOLTIP_ID}
      >
        <i className={classnames(icon, classes.icon)} />
        {notification && <NotificationPin />}
      </GenericButton>
    );
  }

  return (
    <NavLink
      to={to}
      {...rest}
      className={({ isActive }) =>
        classnames(classes.iconContainer, {
          [activeClasses[color]]:
            isActive || others.includes(location.pathname),
          [notActiveClasses[color]]: !isActive && !!emph,
        })
      }
      data-tip={label ? label : undefined}
      data-for={NAVBAR_TOOLTIP_ID}
    >
      <i className={classnames(icon, classes.icon)} />
      {notification && <NotificationPin />}
    </NavLink>
  );
};
